import Logger from '@medflex/utils/Logger';
import qs from 'query-string';
import _ from 'lodash/fp';
import { USER_TYPES } from '@medflex/utils/constants';
import { SessionTypes } from '@medflex/shared-types';
import { merge } from 'lodash';
import makeApiRequest from '../../../api/makeApiRequest';

const TYPE = 'sessions';

const getNewSessions = (currentSessions, sessions, userId) => {
  const newSessions = sessions.filter(s => !currentSessions.some(ps => ps.id === s.id));
  const updatedSessions = currentSessions.reduce((acc, s) => {
    const session = sessions.find(ps => ps.id === s.id);
    const isUserRemoved = userId && session && !session.participants.some(p => p.userId === userId);

    if (!session) return [...acc, s];
    if (isUserRemoved) return acc;

    const participants = session.participants.map(p => {
      const current = s.participants.find(({ participantId }) => participantId === p.participantId);
      if (!current) return p;
      return { ...current, ...p };
    });

    const mergedSession = merge({}, s, session);

    const sessionUpdated = {
      ...mergedSession,
      participants,
    };

    return [...acc, sessionUpdated];
  }, []);

  return [...updatedSessions, ...newSessions];
};

const Sessions = {
  name: TYPE,
  state: {
    sessions: [],
    status: {
      load: 'idle',
    },
  },
  reducers: {
    onParticipantsChanged(state, { participants, sessionId }) {
      const sessionIndex = state.sessions.findIndex(({ id }) => id === sessionId);
      if (sessionIndex === -1) {
        return state;
      }

      const session = state.sessions[sessionIndex];
      const sessionUpdated = {
        ...session,
        participants: participants.map(participant => {
          const currentParticipant = session.participants.find(
            ({ participantId }) => participantId === participant.participantId,
          );
          if (!currentParticipant || !currentParticipant.tan) {
            return participant;
          }

          return {
            ...participant,
            tan: currentParticipant.tan,
          };
        }),
      };
      const sessions = [...state.sessions];
      sessions[sessionIndex] = sessionUpdated;
      return {
        ...state,
        sessions,
      };
    },

    onParticipantRemoved(state, { participantId, sessionId, userId }) {
      const session = state.sessions.find(({ id }) => id === sessionId);
      if (session) {
        return {
          ...state,
          sessions: state.sessions
            .map(s => {
              if (s.id === session.id) {
                const { participants } = session;

                // if self is removed, user no longer has access to chat
                // thus session has to be removed
                if (
                  participants.some(p => p.userId === userId && p.participantId === participantId)
                ) {
                  return null;
                }

                return {
                  ...s,
                  participants: session.participants.filter(p => p.participantId !== participantId),
                };
              }
              return s;
            })
            .filter(s => s !== null),
        };
      }
      return state;
    },
    onLastReadMessageIdChanged(state, { id, sessionId }) {
      const session = state.sessions.find(s => s.id === sessionId);

      if (session) {
        return {
          ...state,
          sessions: state.sessions.map(s => {
            if (s.id === session.id) {
              return {
                ...s,
                messageLastReadId: id,
              };
            }

            return s;
          }),
        };
      }

      return state;
    },
    onLoaded(state, sessions) {
      return {
        ...state,
        sessions,
        status: {
          ...state.status,
          load: 'succeeded',
        },
      };
    },
    onUpdated(state, { sessions, userId }) {
      const newSessions = getNewSessions(state.sessions, sessions, userId);
      return {
        ...state,
        sessions: newSessions,
      };
    },
    onCreated(state, payloadSessions) {
      const newSessions = payloadSessions.filter(
        session => !state.sessions.some(ns => session.id === ns.id),
      );

      return {
        ...state,
        sessions: [...state.sessions, ...newSessions],
      };
    },
    onChatRemoved(state, { id }) {
      const { sessions } = state;
      return { ...state, sessions: sessions.filter(s => s.id !== id) };
    },
    onAuthUpdate(state, { sessionId, ...attributes }) {
      const session = state.sessions.find(({ id }) => id === sessionId);
      return {
        ...state,
        sessions: state.sessions.map(s => {
          if (s.id === session.id) {
            return {
              ...s,
              ...attributes,
            };
          }
          return s;
        }),
      };
    },
    'sessionChats/onMessageSetImportant': (state, { messageId, sessionId, important }) => {
      if (state.sessions.find(({ id }) => id === sessionId)) {
        return {
          ...state,
          sessions: state.sessions.map(session => {
            if (sessionId === session.id) {
              const { messagesMarkedAsImportant = [], ...rest } = session;
              const newMessagesMarkedAsImportant = important
                ? [...messagesMarkedAsImportant, messageId]
                : messagesMarkedAsImportant.filter(message => message !== messageId);

              return {
                ...rest,
                ...(newMessagesMarkedAsImportant.length > 0 && {
                  messagesMarkedAsImportant: newMessagesMarkedAsImportant,
                }),
              };
            }

            return session;
          }),
        };
      }
      return state;
    },
    onSessionChatMessage(state, { id, sessionId, time, sender }, { profile }) {
      if (!sessionId) return { ...state };
      return {
        ...state,
        sessions: state.sessions.map(s => {
          // in case of no hardreload we have to update messageLastReadId on SESSION_MSG_CREATED
          const isOwnMessage =
            s.participants.find(p => p.userId === profile.userId).participantId === sender;

          if (s.id === sessionId) {
            return {
              ...s,
              ...(isOwnMessage ? { messageLastReadId: id } : {}),
              messageLastAt: time,
            };
          }

          return s;
        }),
      };
    },
    onMessageDeleted(state, { sessionId, id }) {
      return {
        ...state,
        sessions: state.sessions.map(s => {
          const { messagesMarkedAsImportant, ...rest } = s;
          if (s.id === sessionId && messagesMarkedAsImportant) {
            const newImportantMsgsArray = messagesMarkedAsImportant.filter(msg => msg !== id);
            return {
              ...rest,
              ...(newImportantMsgsArray.length && {
                messagesMarkedAsImportant: newImportantMsgsArray,
              }),
            };
          }
          return s;
        }),
      };
    },
    onJoinLinkUpdated(state, { joinLink, id }) {
      return {
        ...state,
        sessions: state.sessions.map(s => {
          if (s.id === id) {
            return {
              ...s,
              joinLink,
            };
          }
          return s;
        }),
      };
    },
    onMulticastCreated(state, payload) {
      const upatedSessions = state.sessions.map(s => {
        const sessionMessage = payload.find(({ sessionId }) => sessionId === s.id);
        if (sessionMessage) {
          return {
            ...s,
            messageLastReadId: sessionMessage.id,
            messageLastAt: sessionMessage.time,
          };
        }
        return s;
      });
      return {
        ...state,
        sessions: upatedSessions,
      };
    },
    onRequestUpdated(state, payload) {
      return {
        ...state,
        sessions: state.sessions.map(s => {
          if (s.id === payload.reference?.id) {
            return {
              ...s,
              reference: {
                ...s.reference,
                data: payload,
              },
            };
          }
          return s;
        }),
      };
    },
    onRequestDeleted(state, payload) {
      return {
        ...state,
        sessions: state.sessions.filter(s => {
          return s.reference?.id !== payload.requestId;
        }),
      };
    },
  },
  messageListeners: {
    SESSION_MSG_CREATED({ payload }) {
      this.messageCreated(payload);
    },
    SESSIONS_CREATED({ payload }) {
      this.onCreated(payload);
    },
    SESSIONS_MODIFIED({ payload, state }) {
      this.onUpdated({ sessions: payload, userId: state.superuser.id });
    },
    SESSION_MSG_DELETED({ payload }) {
      this.onMessageDeleted(payload);
    },
    SESSION_MULTI_MSG_CREATED({ payload }) {
      this.multicastMessageCreated(payload);
    },
    'pam:request_updated': function inquiryUpdated({ payload }) {
      this.onRequestUpdated(payload);
    },
    'pam:request_deleted': function inquiryUpdated({ payload }) {
      this.onRequestDeleted(payload);
    },
  },
  effects: dispatch => ({
    async load(__, { oidcSession, superuser }) {
      const isPatientUser = oidcSession.userType === USER_TYPES.PATIENT;
      const typesToRequest = isPatientUser
        ? []
        : Object.values(_.omitBy(type => type === SessionTypes.PM_CHAT)(SessionTypes));
      const url = `/sessions?userIds=${superuser.id}${
        typesToRequest.length
          ? `&${qs.stringify({ types: typesToRequest }, { arrayFormat: 'bracket' })}`
          : ''
      }`;

      const [request] = makeApiRequest({ url, method: 'get' }, oidcSession);
      const { error, data } = await request;
      if (!error) {
        this.onLoaded(data.data);
      }
    },
    async find({ id, participantId, tan }) {
      const url = `/sessions/${id}/${participantId}?tan=${tan}`;
      const [request] = makeApiRequest({ url, method: 'get' }, null, { internal: false });
      const { error, data } = await request;

      if (!error) {
        const { attributes } = data.data;
        this.onUpdated({ sessions: [{ id, ...attributes }] });
      }

      dispatch.errors.add({ realm: 'sessions', type: 'find', error });
    },
    async create(
      { type, scheduledAt, title, participants, scheduledEndedAt, description, anonymous },
      { oidcSession },
    ) {
      dispatch.errors.remove({ realm: 'sessions', type: 'create' });
      const body = {
        data: {
          type: TYPE,
          attributes: {
            type,
            scheduledAt,
            ...(title && { title }),
            ...(scheduledEndedAt && { scheduledEndedAt }),
            ...(description && { description }),
            participants,
            anonymous,
          },
        },
      };

      const [request] = makeApiRequest(
        { url: '/sessions', method: 'post', data: body },
        oidcSession,
      );
      const { error, data } = await request;
      if (!error) {
        const { id, attributes } = data.data;
        this.onCreated([
          {
            id,
            ...attributes,
          },
        ]);
        return;
      }

      dispatch.errors.add({ realm: 'sessions', type: 'create', error });
    },
    async resendInvitation(sessionId, { oidcSession }) {
      const [request] = makeApiRequest(
        { url: `/sessions/${sessionId}/resend-invitation`, method: 'patch' },
        oidcSession,
      );

      const { error, data } = await request;

      if (error) {
        throw error;
      }

      return data;
    },
    async messageCreated(payload, { oidcSession }) {
      this.onSessionChatMessage(payload, oidcSession);
    },
    async multicastMessageCreated(payload, { oidcSession }) {
      this.onMulticastCreated(payload, oidcSession);
    },
    async createChat({ title, participants, expires, type }, { oidcSession }) {
      dispatch.errors.remove({ realm: 'sessions', type: 'createChat' });
      const body = {
        data: {
          type: TYPE,
          attributes: {
            type: type || 'chat',
            title,
            participants,
            expires,
          },
        },
      };

      const [request] = makeApiRequest(
        { url: '/sessions', method: 'post', data: body },
        oidcSession,
      );
      const { error, data } = await request;
      if (!error) {
        const { id, attributes } = data.data;
        this.onCreated([
          {
            id,
            ...attributes,
          },
        ]);
        return;
      }

      dispatch.errors.add({ realm: 'sessions', type: 'createChat', error });
      throw error;
    },
    async removeChat({ id }, { oidcSession }) {
      dispatch.errors.remove({ realm: 'sessions', type: 'removeChat' });
      const [request] = makeApiRequest({ url: `/sessions/${id}`, method: 'delete' }, oidcSession);

      const { error } = await request;
      if (!error) {
        this.onChatRemoved({ id });
        return;
      }

      dispatch.errors.add({ realm: 'sessions', type: 'removeChat', error });
    },
    async update({ id, participantId, tan, attributes }, { oidcSession }) {
      dispatch.errors.remove({ realm: 'sessions', type: 'update' });
      const body = {
        data: {
          type: TYPE,
          id,
          attributes,
        },
      };
      const url = `/sessions/${id}/${participantId}?tan=${tan}`;
      const [request] = makeApiRequest({ url, method: 'patch', data: body }, oidcSession, {
        internal: false,
      });
      const { error, data } = await request;
      if (!error) {
        const { attributes: patchedAttributes } = data.data;
        this.onUpdated({ sessions: [{ id, ...patchedAttributes }] });
        return;
      }
      dispatch.errors.add({ realm: 'sessions', type: 'update', error });
    },
    async authUpdate({ sessionId, ...attributes }, { oidcSession }) {
      dispatch.errors.remove({ realm: 'sessions', type: 'authUpdate' });
      const body = {
        data: {
          type: TYPE,
          attributes,
        },
      };
      const url = `/sessions/${sessionId}`;
      const [request] = makeApiRequest({ url, method: 'patch', data: body }, oidcSession);
      const { error } = await request;
      if (error) {
        dispatch.errors.add({ realm: 'sessions', type: 'authUpdate', error });
        throw error;
      }
      this.onAuthUpdate({ sessionId, ...attributes });
    },
    async removeParticipant({ participantId, sessionId }, { oidcSession }) {
      dispatch.errors.remove({ realm: 'sessions', type: 'removeParticipant' });
      const url = `/sessions/${sessionId}/${participantId}`;
      const [request] = makeApiRequest({ url, method: 'delete' }, oidcSession);
      const { error } = await request;
      if (error) {
        dispatch.errors.add({ realm: 'sessions', type: 'removeParticipant', error });
        throw error;
      }
      this.onParticipantRemoved({ sessionId, participantId, userId: oidcSession.profile.userId });
    },
    async addParticipant({ sessionId, participant }, { oidcSession }) {
      dispatch.errors.remove({ realm: 'sessions', type: 'addParticipant' });
      const body = {
        data: {
          type: 'participants',
          attributes: {
            ...participant,
          },
        },
      };
      const url = `/sessions/${sessionId}/participants`;
      const [request] = makeApiRequest({ url, method: 'post', data: body }, oidcSession);
      const { error, data } = await request;
      if (error) {
        dispatch.errors.add({ realm: 'sessions', type: 'addParticipant', error });
        throw error;
      }

      const { participants = [] } = data.data.attributes;

      this.onParticipantsChanged({
        sessionId,
        participants,
      });
    },
    async changeModerator({ sessionId, moderatorId }, { oidcSession }) {
      dispatch.errors.remove({ realm: 'sessions', type: 'changeModerator' });
      const body = {
        data: {
          type: 'moderator',
          moderatorId,
        },
      };
      const url = `/sessions/${sessionId}/moderator`;
      const [request] = makeApiRequest({ url, method: 'patch', data: body }, oidcSession);
      const { error, data } = await request;
      if (error) {
        dispatch.errors.add({ realm: 'sessions', type: 'changeModerator', error });
        throw error;
      }

      const { participants = [] } = data.data.attributes;

      this.onParticipantsChanged({
        sessionId,
        participants,
      });
    },
    async setLastReadMessageId({ id, messageId }, { oidcSession }) {
      const url = `/sessions/${id}/last-read-message/${messageId || 'null'}`;
      const [request] = makeApiRequest({ url, method: 'patch' }, oidcSession);
      const { error } = await request;

      if (!error) {
        this.onLastReadMessageIdChanged({ id: messageId, sessionId: id });
        return;
      }

      dispatch.errors.add({ realm: 'sessions', type: 'setLastReadMessageId', error });
      Logger.error(error);
    },
    async updateJoinLink({ id }, { oidcSession }) {
      dispatch.errors.remove({ realm: 'sessions', type: 'updateJoinLink' });
      const url = `/sessions/${id}/permanent-link`;
      const [request] = makeApiRequest({ url, method: 'patch' }, oidcSession);
      const { error, data } = await request;
      if (!error) {
        const { attributes, id: sessionId } = data.data;
        this.onJoinLinkUpdated({ id: sessionId, joinLink: attributes.joinLink });
        return;
      }

      dispatch.errors.add({ realm: 'sessions', type: 'updateJoinLink', error });
      Logger.error(error);
    },
    async getOneTimePassword({ sessionId, participantId }, { oidcSession }) {
      const url = `/sessions/${sessionId}/${participantId}/otp`;
      const [request] = makeApiRequest({ url, method: 'get' }, oidcSession);
      const { error, data } = await request;

      if (error) {
        throw error;
      }

      return data.data;
    },
  }),
};

export default Sessions;
